import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock, Mail, LogOut, MessageCircle, CheckCircle2, User } from "lucide-react";

/**
 * PendingActivation — friendly waiting screen for new Google users
 * whose account has been created but hasn't been assigned to any company yet.
 */
export default function PendingActivation() {
  const { user, logout } = useAuth();
  const adminEmail = "muazmusellem.workmail@gmail.com";
  const adminPhone = ""; // pull from env or config if desired
  const waMessage = encodeURIComponent(
    `مرحباً، أنا ${user?.name || ""} (${user?.email || ""}) — سجّلت دخول عبر Google وأنتظر تفعيل حسابي على محاسبتي. شكراً.`
  );

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-primary/5 via-background to-emerald-50/40 p-6" data-testid="pending-activation-page">
      <div className="max-w-lg w-full bg-card border-2 border-primary/20 rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 grid place-items-center mb-4 relative">
            <Clock className="w-10 h-10 text-primary" />
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border-2 border-primary grid place-items-center">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <h1 className="font-heading font-bold text-2xl mb-2">مرحباً بك في محاسبتي 👋</h1>
          <p className="text-muted-foreground text-sm">تم إنشاء حسابك بنجاح — بانتظار موافقة الإدارة لتفعيل الوصول</p>
        </div>

        <div className="space-y-3 mb-6">
          {user?.picture && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border">
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full border-2 border-primary/20" />
              <div>
                <div className="font-bold">{user.name}</div>
                <div className="text-xs text-muted-foreground">{user.email}</div>
              </div>
            </div>
          )}
          {!user?.picture && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border">
              <div className="w-12 h-12 rounded-full bg-primary/10 grid place-items-center"><User className="w-6 h-6 text-primary" /></div>
              <div>
                <div className="font-bold">{user?.name || "—"}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
              </div>
            </div>
          )}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <div className="font-bold mb-1">⏳ ما الخطوة التالية؟</div>
            <ul className="text-xs space-y-1 list-disc me-4">
              <li>الإدارة أُخطرت تلقائياً بطلبك</li>
              <li>سيتم ربط حسابك بشركة وتحديد صلاحياتك</li>
              <li>ستستلم إيميلاً فور التفعيل</li>
              <li>عادة يستغرق الأمر أقل من 24 ساعة</li>
            </ul>
          </div>
        </div>

        <div className="space-y-2">
          <a href={`mailto:${adminEmail}?subject=استفسار عن تفعيل حسابي على محاسبتي&body=مرحباً،%0A%0Aاسمي ${user?.name || ""} وبريدي ${user?.email || ""}.%0Aسجّلت دخول عبر Google وأنتظر تفعيل حسابي.%0A%0Aشكراً.`} className="w-full">
            <Button variant="outline" className="w-full gap-2 h-11" data-testid="contact-email-btn">
              <Mail className="w-4 h-4" /> راسل الإدارة بالبريد
            </Button>
          </a>
          {adminPhone && (
            <a href={`https://wa.me/${adminPhone}?text=${waMessage}`} target="_blank" rel="noopener" className="w-full">
              <Button variant="outline" className="w-full gap-2 h-11 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                <MessageCircle className="w-4 h-4" /> راسلنا على واتساب
              </Button>
            </a>
          )}
          <Button variant="ghost" className="w-full gap-2" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> خروج
          </Button>
        </div>

        <div className="mt-6 pt-4 border-t text-center text-[10px] text-muted-foreground">
          <button onClick={() => window.location.reload()} className="hover:underline">🔄 تحقّق من حالة التفعيل</button>
        </div>
      </div>
    </div>
  );
}
